import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Slider,
  Box,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { buildShareUrl } from '../../utils/shareUrl';
import {
  PlayArrow,
  Pause,
  SkipNext,
  SkipPrevious,
  FavoriteBorder,
  Favorite,
  QueueMusic,
  Link,
  Share,
  MoreVert,
} from '@mui/icons-material';
import { usePlayerStore } from '../../stores/playerStore';
import { useFavoritesStore } from '../../stores/favoritesStore';
import { useSettingsStore } from '../../stores/settingsStore';
import { useMemo, useCallback, useState } from 'react';

interface PlayerBarProps {
  isMobile?: boolean;
}

const PlayerBar = ({ isMobile = false }: PlayerBarProps) => {
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(menuAnchor);
  const [dragPosition, setDragPosition] = useState<number | null>(null);

  const {
    queue,
    currentIndex,
    isPlaying,
    currentPosition,
    duration,
    play,
    pause,
    skipToNext,
    skipToPrevious,
    seekTo,
  } = usePlayerStore();

  const playbackBlocked = usePlayerStore((state) => state.playbackBlocked);
  const isLoadingFromShare = usePlayerStore((state) => state.isLoadingFromShare);

  const { addToFavorites, isFavorite } = useFavoritesStore();
  const { addFullShowAfterCurrentSong } = usePlayerStore();
  const { fullShowMode, continueJamsMode } = useSettingsStore();

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchor(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const currentItem = useMemo(() => {
    return currentIndex >= 0 && currentIndex < queue.length ? queue[currentIndex] : null;
  }, [queue, currentIndex]);

  const isCurrentFavorite = useMemo(() => {
    return currentItem ? isFavorite(currentItem.id) : false;
  }, [currentItem, isFavorite]);

  const handleToggleFavorite = useCallback(() => {
    if (currentItem) {
      addToFavorites(currentItem);
    }
  }, [currentItem, addToFavorites]);

  const handleAddRestOfShow = useCallback(async () => {
    if (!currentItem) return;
    const collection = currentItem.extras?.collection || currentItem.artist || '';
    const showId = currentItem.extras?.show_id || currentItem.extras?.showId;
    const track = currentItem.extras?.track;
    const source = currentItem.extras?.source || 'ls';
    if (showId && track) {
      await addFullShowAfterCurrentSong(collection, showId, track, source);
    }
  }, [currentItem, addFullShowAfterCurrentSong]);

  const handleViewSource = useCallback(() => {
    if (currentItem?.genre) {
      window.open(currentItem.genre, '_blank');
    }
  }, [currentItem]);

  const handleShare = useCallback(async () => {
    if (!currentItem) return;

    const url = buildShareUrl({ item: currentItem, fullShowMode, continueJamsMode });

    try {
      // Only use native share on mobile devices - desktop share sheets often lack copy option
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      if (navigator.share && isMobile) {
        await navigator.share({
          title: `${currentItem.title} - ${currentItem.artist}`,
          url,
        });
      } else {
        await navigator.clipboard.writeText(url);
        // Could add a toast notification here
      }
    } catch (err) {
      // User cancelled or error - silently fail
      console.log('Share cancelled or failed:', err);
    }
  }, [currentItem, fullShowMode, continueJamsMode]);

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (!currentItem) {
    return (
      <AppBar
        position="fixed"
        sx={{
          top: 'auto',
          bottom: 0,
          bgcolor: 'background.paper',
          borderTop: '1px solid',
          borderColor: 'divider',
          height: isMobile ? 72 : 90,
        }}
      >
        <Toolbar sx={{
          minHeight: isMobile ? 72 : 90,
          justifyContent: 'center',
        }}>
          <Typography variant="body2" color="text.secondary">
            Select bands and click Start Playing
          </Typography>
        </Toolbar>
      </AppBar>
    );
  }

  const displayPosition = dragPosition ?? currentPosition;

  // Show warning when playback is blocked for non-share playback
  const showBlockedWarning = playbackBlocked && !isLoadingFromShare;

  return (
    <>
      {showBlockedWarning && (
        <Box
          sx={{
            position: 'fixed',
            bottom: isMobile ? 72 : 90,
            left: 0,
            right: 0,
            bgcolor: 'warning.main',
            color: 'warning.contrastText',
            py: 1,
            px: 2,
            textAlign: 'center',
            zIndex: 1100,
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            Playback blocked by browser. Click the play button to start.
          </Typography>
        </Box>
      )}
      <AppBar
      position="fixed"
      sx={{
        top: 'auto',
        bottom: 0,
        bgcolor: 'background.paper',
        borderTop: '1px solid',
        borderColor: 'divider',
        height: isMobile ? 72 : 90,
      }}
    >
      {/* Seek bar at top */}
      <Slider
        aria-label="Seek"
        size="small"
        value={duration > 0 ? Math.min(displayPosition, duration) : 0}
        min={0}
        max={duration > 0 ? duration : 1}
        disabled={duration <= 0}
        onChange={(_, value) => setDragPosition(value as number)}
        onChangeCommitted={(_, value) => {
          seekTo(value as number);
          setDragPosition(null);
        }}
        sx={{
          position: 'absolute',
          top: -5,
          left: 0,
          right: 0,
          width: '100%',
          height: 3,
          p: '5px 0',
          borderRadius: 0,
          color: 'primary.main',
          zIndex: 1,
          '@media (pointer: coarse)': {
            p: '5px 0',
          },
          '& .MuiSlider-rail': {
            bgcolor: 'action.hover',
            opacity: 1,
          },
          '& .MuiSlider-track': {
            border: 'none',
          },
          '& .MuiSlider-thumb': {
            width: 12,
            height: 12,
            opacity: dragPosition !== null ? 1 : 0,
            transition: 'opacity 0.2s',
            '&:hover, &.Mui-focusVisible, &.Mui-active': {
              opacity: 1,
              boxShadow: 'none',
            },
          },
          '&:hover .MuiSlider-thumb': {
            opacity: 1,
          },
        }}
      />
      
      <Toolbar 
        sx={{ 
          minHeight: isMobile ? 72 : 90, 
          gap: isMobile ? 1 : 2,
          px: isMobile ? 1 : 2,
        }}
      >
        {/* Song Info - takes available space */}
        <Box 
          sx={{ 
            flexGrow: 1, 
            display: 'flex', 
            alignItems: 'center', 
            gap: isMobile ? 1 : 3,
            minWidth: 0, // Important for text truncation
          }}
        >
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography 
              variant={isMobile ? "body2" : "body1"} 
              noWrap 
              sx={{ 
                fontWeight: 600, 
                color: 'text.primary',
                fontSize: isMobile ? '0.875rem' : '1rem',
              }}
            >
              {currentItem.title}
            </Typography>
            <Typography 
              variant="caption" 
              color="text.secondary" 
              noWrap 
              sx={{ 
                opacity: 0.8,
                display: 'block',
              }}
            >
              {currentItem.artist} • {currentItem.album}
            </Typography>
          </Box>

          {/* Time Display */}
          <Typography 
            variant="caption" 
            color="text.secondary" 
            sx={{ 
              minWidth: 'fit-content', 
              fontWeight: 500,
              fontSize: isMobile ? '0.75rem' : '0.875rem',
            }}
          >
            {formatTime(displayPosition)} / {formatTime(duration)}
          </Typography>
        </Box>

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: isMobile ? 0 : 0.5 }}>
          {!isMobile && (
            <>
              <IconButton
                onClick={handleToggleFavorite}
                size="medium"
                title={isCurrentFavorite ? 'Already in favorites' : 'Add to favorites'}
                sx={{
                  color: isCurrentFavorite ? 'error.main' : 'text.secondary',
                  '&:hover': { color: 'error.main' },
                }}
              >
                {isCurrentFavorite ? <Favorite /> : <FavoriteBorder />}
              </IconButton>
              <IconButton
                onClick={handleViewSource}
                size="medium"
                title="View source"
                disabled={!currentItem.genre}
                sx={{
                  color: 'text.secondary',
                  '&:hover': { color: 'primary.main' },
                }}
              >
                <Link />
              </IconButton>
              <IconButton
                onClick={handleShare}
                size="medium"
                title="Share song"
                sx={{
                  color: 'text.secondary',
                  '&:hover': { color: 'primary.main' },
                }}
              >
                <Share />
              </IconButton>
              <IconButton
                onClick={handleAddRestOfShow}
                size="medium"
                title="Add rest of show to queue"
                sx={{
                  color: 'text.secondary',
                  '&:hover': { color: 'primary.main' },
                }}
              >
                <QueueMusic />
              </IconButton>
            </>
          )}
          {isMobile && (
            <>
              <IconButton
                onClick={handleMenuOpen}
                size="small"
                sx={{
                  color: 'text.secondary',
                  '&:hover': { color: 'primary.main' },
                }}
              >
                <MoreVert />
              </IconButton>
              <Menu
                anchorEl={menuAnchor}
                open={menuOpen}
                onClose={handleMenuClose}
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                PaperProps={{
                  sx: {
                    minWidth: 200,
                  },
                }}
              >
                <MenuItem
                  onClick={() => {
                    handleToggleFavorite();
                    handleMenuClose();
                  }}
                >
                  <ListItemIcon>
                    {isCurrentFavorite ? (
                      <Favorite fontSize="small" color="error" />
                    ) : (
                      <FavoriteBorder fontSize="small" />
                    )}
                  </ListItemIcon>
                  <ListItemText>
                    {isCurrentFavorite ? 'Favorited' : 'Add to favorites'}
                  </ListItemText>
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    handleViewSource();
                    handleMenuClose();
                  }}
                  disabled={!currentItem.genre}
                >
                  <ListItemIcon>
                    <Link fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>View source</ListItemText>
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    handleShare();
                    handleMenuClose();
                  }}
                >
                  <ListItemIcon>
                    <Share fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Share song</ListItemText>
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    handleAddRestOfShow();
                    handleMenuClose();
                  }}
                >
                  <ListItemIcon>
                    <QueueMusic fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Add rest of show</ListItemText>
                </MenuItem>
              </Menu>
            </>
          )}
        </Box>

        {/* Playback Controls */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: isMobile ? 0 : 0.5 }}>
          <IconButton
            onClick={skipToPrevious}
            size={isMobile ? "small" : "medium"}
            sx={{
              color: 'text.primary',
              '&:hover': { bgcolor: 'action.hover' },
            }}
          >
            <SkipPrevious />
          </IconButton>

          <IconButton
            onClick={isPlaying ? pause : play}
            size={isMobile ? "small" : "medium"}
            sx={{
              color: 'primary.main',
              bgcolor: 'rgba(66, 165, 245, 0.1)',
              mx: isMobile ? 0.25 : 0.5,
              '&:hover': {
                bgcolor: 'rgba(66, 165, 245, 0.15)',
              },
            }}
          >
            {isPlaying ? <Pause /> : <PlayArrow />}
          </IconButton>

          <IconButton
            onClick={skipToNext}
            size={isMobile ? "small" : "medium"}
            sx={{
              color: 'text.primary',
              '&:hover': { bgcolor: 'action.hover' },
            }}
          >
            <SkipNext />
          </IconButton>
        </Box>
      </Toolbar>
    </AppBar>
    </>
  );
};

export default PlayerBar;
